import { t } from 'i18next';
import _ from 'lodash';
import { useMemo } from 'react';
import { useAllSpecs, useClassesById } from '../../../selectors/global';
import { TClassSummary, TSpecSummary } from '../../../../../types/utilityTypes';
import { RioSelectOption } from '../../../../components/presentational/select/types';
import { IOptionLabelViewConfig, TClassSelectViewOptions, TSpecSelectViewOptions } from './types';
import { ExtendedSpecRole } from '../../../../../types/wowTypes';
import { isSpecMatchRole } from '../../../../../common/wow';

export const DEFAULT_SPEC_VIEW_OPTIONS: Required<TSpecSelectViewOptions> = {
  classColor: false,
  menuValue: {
    clsName: true,
    specName: 'name'
  },
  selectedValue: {
    clsName: false,
    specName: 'short-name'
  }
};

export const DEFAULT_CLASS_VIEW_OPTIONS: Required<TClassSelectViewOptions> = {
  classColor: false
};

export const formatSpecLabelWithViewConfig = (
  spec: TSpecSummary,
  cls: TClassSummary,
  config: IOptionLabelViewConfig
) => {
  const { clsName, specName } = config;

  if (!clsName) {
    return t(`game:specs.${cls?.slug}.${spec.slug}.${specName}`);
  }

  return t('common:class-spec', {
    class: t(`game:classes.${cls?.slug}.name`),
    spec: t(`game:specs.${cls?.slug}.${spec.slug}.${specName}`)
  });
};

export const useSpecOptions = (filterByRoles?: ExtendedSpecRole[], classId?: number, specIds?: number[]) => {
  const allSpecs = useAllSpecs();
  const classesById = useClassesById();

  const specOptions: RioSelectOption<TSpecSummary>[] = useMemo(() => {
    let filteredSpecs = filterByRoles
      ? allSpecs.filter((spec) => filterByRoles.some((role) => isSpecMatchRole(spec, role)))
      : allSpecs;

    if (classId != null) {
      filteredSpecs = filteredSpecs.filter((spec) => spec.class_id === classId);
    }

    // An explicit id allowlist — for callers whose valid set is data-driven rather
    // than a whole class or role (e.g. only the specs that carry a given talent).
    if (specIds) {
      const allowedSpecIds = new Set(specIds);
      filteredSpecs = filteredSpecs.filter((spec) => allowedSpecIds.has(spec.id));
    }

    const groupedSpecs = _.groupBy(filteredSpecs, 'class_id');

    const groupedByClass = Object.values(groupedSpecs)
      .map((specs) => {
        const cls = classesById[specs[0].class_id];

        return {
          label: t(`game:classes.${cls.slug}.name`),
          options: _.sortBy(specs, 'name')
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    if (groupedByClass.length === 1) {
      return groupedByClass[0].options;
    }

    return groupedByClass;
  }, [allSpecs, classesById, classId, filterByRoles, specIds]);

  return specOptions;
};
